import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Collapse,
  Chip,
  Stack,
  Link as MuiLink,
} from '@mui/material';
import { Visibility, VisibilityOff, LockOutlined, SecurityOutlined, PersonAddOutlined } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import api from '../../services/api';
import axios from 'axios';
import { useNotification } from '../../context/NotificationContext';
import AuthLayout from '../../components/layout/AuthLayout';

const SHOW_DEMO_CREDENTIALS = process.env.REACT_APP_SHOW_DEMO_CREDENTIALS === 'true';

const cardSx = {
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
  bgcolor: 'rgba(20, 20, 30, 0.58)',
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  height: '100%',
};

const inputFieldSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
  },
};

const iconBadgeSx = (bgColor: string) => ({
  width: 52,
  height: 52,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: bgColor,
  mb: 2,
});

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();
  const { showNotification } = useNotification();
  const { settings, loading: settingsLoading } = usePublicSettings();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDemoCredentials, setShowDemoCredentials] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      const home = user.role === 'admin' ? '/admin' : user.role === 'instructor' ? '/instructor' : '/student';
      navigate(home, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('error');
    if (ssoError) {
      setError(`SSO login failed: ${decodeURIComponent(ssoError)}`);
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 7000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const validateEmail = (value: string) => {
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (emailError || !email || !password) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      login(access_token, userData);
      showNotification('Login successful', 'success');
      const dest = userData.role === 'admin' ? '/admin' : userData.role === 'instructor' ? '/instructor' : '/student';
      navigate(dest, { replace: true });
    } catch (err) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setError(message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fillDemoCredentials = (type: 'instructor' | 'student') => {
    if (type === 'instructor') {
      setEmail(process.env.REACT_APP_DEMO_INSTRUCTOR_EMAIL || 'instructor@praxis-eval.com');
      setPassword(process.env.REACT_APP_DEMO_INSTRUCTOR_PASSWORD || 'Workshop@123');
    } else {
      setEmail(process.env.REACT_APP_DEMO_STUDENT_EMAIL || 'student1@praxis-eval.com');
      setPassword(process.env.REACT_APP_DEMO_STUDENT_PASSWORD || 'Student@123');
    }
    setEmailError('');
  };

  const handleSsoClick = () => {
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    window.location.href = `${apiBase}/auth/oidc`;
  };

  // Loading state — show just logo + spinner (no layout flash)
  if (settingsLoading) {
    return (
      <AuthLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: 'rgba(255,255,255,0.6)' }} />
        </Box>
      </AuthLayout>
    );
  }

  const selfRegEnabled = settings?.selfRegistrationEnabled ?? false;

  // --- Sign In form fields (shared) ---
  const signInForm = (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <TextField
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => validateEmail(email)}
        error={!!emailError}
        helperText={emailError}
        disabled={loading}
        margin="normal"
        variant="outlined"
        InputProps={{
          sx: { height: 48, fontSize: 16, borderRadius: '8px', color: '#fff' },
        }}
        InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
        sx={inputFieldSx}
      />
      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={loading}
        margin="normal"
        variant="outlined"
        InputProps={{
          sx: { height: 48, fontSize: 16, borderRadius: '8px', color: '#fff' },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                size="small"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                sx={{ color: 'rgba(255,255,255,0.5)' }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
        sx={inputFieldSx}
      />
      <Button
        fullWidth
        type="submit"
        variant="contained"
        color="primary"
        disabled={loading || !email || !password || !!emailError}
        sx={{ mt: 3, height: 48, fontSize: 16, fontWeight: 500, borderRadius: '8px' }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
      </Button>

      <Box sx={{ mt: 1.5, textAlign: 'center' }}>
        <MuiLink
          component={Link}
          to="/forgot-password"
          sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}
        >
          Forgot Password?
        </MuiLink>
      </Box>
    </Box>
  );

  // --- SSO Button ---
  const ssoButton = (
    <Button
      fullWidth
      variant="outlined"
      onClick={handleSsoClick}
      disabled={loading}
      sx={{
        height: 48,
        fontSize: 16,
        fontWeight: 500,
        borderRadius: '8px',
        color: '#fff',
        borderColor: 'rgba(255,255,255,0.3)',
        '&:hover': {
          borderColor: 'rgba(255,255,255,0.6)',
          bgcolor: 'rgba(255,255,255,0.05)',
        },
      }}
    >
      Sign in with CyberArk SSO
    </Button>
  );

  // --- Demo credentials ---
  const demoSection = SHOW_DEMO_CREDENTIALS && (
    <Box sx={{ mt: 3, textAlign: 'center' }}>
      <Button
        variant="text"
        size="small"
        onClick={() => setShowDemoCredentials(!showDemoCredentials)}
        sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}
      >
        Demo Credentials
      </Button>
      <Collapse in={showDemoCredentials}>
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
          <Chip
            label="Instructor"
            size="small"
            color="primary"
            variant="outlined"
            onClick={() => fillDemoCredentials('instructor')}
          />
          <Chip
            label="Student"
            size="small"
            color="secondary"
            variant="outlined"
            onClick={() => fillDemoCredentials('student')}
          />
        </Stack>
      </Collapse>
    </Box>
  );

  // ===== SINGLE-CARD LAYOUT (feature flag OFF) =====
  if (!selfRegEnabled) {
    return (
      <AuthLayout maxWidth="420px">
        <Card sx={cardSx}>
          <CardContent sx={{ p: '48px' }}>
            {signInForm}

            <Divider sx={{ my: 2, '&::before, &::after': { borderColor: 'rgba(255,255,255,0.15)' } }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>or</Typography>
            </Divider>

            {ssoButton}
            {demoSection}
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // ===== 3-CARD LAYOUT (feature flag ON) =====
  return (
    <AuthLayout maxWidth="1100px">
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'center', md: 'stretch' },
        }}
      >
        {/* Sign In Card */}
        <Box sx={{ flex: 1, maxWidth: { xs: '420px', md: 'none' }, width: '100%' }}>
          <Card sx={cardSx}>
            <CardContent sx={{ p: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={iconBadgeSx('#7c4dff')}>
                <LockOutlined sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight={600} color="#fff" sx={{ mb: 2 }}>
                Sign In
              </Typography>
              <Box sx={{ width: '100%' }}>
                {signInForm}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* SSO Card */}
        <Box sx={{ flex: 1, maxWidth: { xs: '420px', md: 'none' }, width: '100%' }}>
          <Card sx={cardSx}>
            <CardContent sx={{ p: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Box sx={iconBadgeSx('#1e88e5')}>
                <SecurityOutlined sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight={600} color="#fff" sx={{ mb: 2 }}>
                CyberArk SSO
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
                Sign in with your corporate identity using CyberArk Single Sign-On.
              </Typography>
              <Box sx={{ width: '100%' }}>
                {ssoButton}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Register Card */}
        <Box sx={{ flex: 1, maxWidth: { xs: '420px', md: 'none' }, width: '100%' }}>
          <Card sx={cardSx}>
            <CardContent sx={{ p: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Box sx={iconBadgeSx('#43a047')}>
                <PersonAddOutlined sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight={600} color="#fff" sx={{ mb: 2 }}>
                Register
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
                Create a new account using your corporate email address.
              </Typography>
              <Box sx={{ width: '100%' }}>
                <Button
                  fullWidth
                  variant="outlined"
                  component={Link}
                  to="/register"
                  sx={{
                    height: 48,
                    fontSize: 16,
                    fontWeight: 500,
                    borderRadius: '8px',
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.3)',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.6)',
                      bgcolor: 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  Register &rarr;
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Privacy Policy footer */}
      <Typography
        variant="body2"
        sx={{ color: 'rgba(255,255,255,0.85)', textAlign: 'center', mt: 3 }}
      >
        By signing in or registering, you agree to our{' '}
        <MuiLink
          component={Link}
          to="/privacy-policy"
          sx={{ color: '#90caf9', fontWeight: 500, textDecoration: 'underline', '&:hover': { color: '#fff' } }}
        >
          Privacy Policy
        </MuiLink>
        .
      </Typography>

      {/* Demo credentials below cards */}
      {demoSection}
    </AuthLayout>
  );
};

export default LoginPage;
